import { getOperationName } from '@apollo/client/utilities';
import { useMemo, useCallback } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums';
import { useAuth } from 'shared/providers/AuthProvider';
import {
  ClinicUserClinicFragment,
  ClinicUserFragment,
  GetClinicUserDocument,
  PermissionType,
  useGetClinicUserQuery,
} from 'shared/types/graphql';
import { hasClinicAdminRole, hasCompanyEmail, hasTeletriageRole } from 'shared/utils/authorization';
import { useAppSelector } from 'state/hooks';

export const getClinicUserQuery = getOperationName(GetClinicUserDocument) || '';

const useClinicUser = (): {
  clinicUser: ClinicUserFragment | null;
  clinicUserId: string | undefined;
  currentClinic: ClinicUserClinicFragment | null;
  currentClinicId: string | undefined;
  userHasPermission: (permission: PermissionType, clinicId?: string | null | undefined) => boolean;
  isOttoTeamMember: boolean;
  isOttoGlobalAdmin: boolean;
  hasAdminRoleAtCurrentClinic: boolean;
  isTeletriageAgent: boolean;
  isLoadingUserData: boolean;
  refetchClinicUser: () => void;
  currentClinicActiveSubscription?: ClinicUserClinicFragment['productPackageSubscriptions'][number];
} => {
  const { isAuthenticated, isImpersonating } = useAuth();

  const { data, loading, refetch } = useGetClinicUserQuery({
    skip: !isAuthenticated,
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });

  const clinicUser = data?.clinicUser ?? null;
  const clinicUserId = clinicUser?.id;
  const impersonationClinicId = useAppSelector((state) => state.currentClinic.clinicId);
  const currentClinicId = useMemo(() => {
    if (isImpersonating && impersonationClinicId) {
      return impersonationClinicId;
    }
    return data?.clinicUser?.vetInfo?.currentClinic?.id;
  }, [data?.clinicUser?.vetInfo?.currentClinic?.id, isImpersonating, impersonationClinicId]);

  const currentClinic = useMemo(() => {
    return clinicUser?.clinics.find(({ id }) => id === currentClinicId) ?? null;
  }, [clinicUser, currentClinicId]);

  const currentClinicActiveSubscription = useMemo(() => {
    return currentClinic?.productPackageSubscriptions.find((subscription) => {
      return subscription.endAt === null || new Date(subscription.endAt) > new Date();
    });
  }, [currentClinic?.productPackageSubscriptions]);

  const userHasPermission = useCallback(
    (permission: PermissionType, clinicId?: string | null | undefined): boolean => {
      const allRoles = clinicUser?.vetInfo?.roles || [];
      clinicId = clinicId || currentClinicId;
      if (!clinicId || !allRoles.length) return false;

      let permissions: PermissionType[] = [];
      const roles = allRoles.filter(({ clinic }) => clinic?.id && clinic.id === clinicId) || [];
      roles.forEach((role) => {
        permissions = [...permissions, ...(role.permissions || []).map(({ type }) => type)];
      });
      return permissions.indexOf(permission) !== -1;
    },
    [currentClinicId, clinicUser],
  );

  const isOttoTeamMember = useMemo(() => hasCompanyEmail(clinicUser?.email), [clinicUser]);

  const isOttoGlobalAdmin = useMemo(
    () => isOttoTeamMember && !!clinicUser?.isAdmin,
    [clinicUser?.isAdmin, isOttoTeamMember],
  );

  const hasAdminRoleAtCurrentClinic = useMemo(
    () => hasClinicAdminRole(clinicUser?.vetInfo?.roles, currentClinicId),
    [clinicUser?.vetInfo?.roles, currentClinicId],
  );

  const isTeletriageAgent = useMemo(
    () => hasTeletriageRole(clinicUser?.vetInfo?.roles, currentClinicId),
    [clinicUser?.vetInfo?.roles, currentClinicId],
  );

  return {
    clinicUser,
    clinicUserId,
    currentClinic,
    currentClinicId,
    userHasPermission,
    isOttoTeamMember,
    isOttoGlobalAdmin,
    hasAdminRoleAtCurrentClinic,
    isTeletriageAgent,
    isLoadingUserData: loading,
    refetchClinicUser: refetch,
    currentClinicActiveSubscription,
  };
};

export default useClinicUser;
